import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DataTable, Stat } from '../components/DataTable';
import { ErrorLine } from '../components/ErrorLine';
import { LoadingLine } from '../components/LoadingLine';
import { BannerHead, PageBanner } from '../components/PageBanner';
import { SectionHead } from '../components/SectionHead';
import { Select } from '../components/Select';
import { api, fmtSigned, type League, type LeaderboardRow } from '../lib/api';
import { logo } from '../lib/logo';
import { teamColor } from '../lib/colors';

const PHASES: { id: string; label: string; what: string }[] = [
  { id: 'head_coach', label: 'Game management', what: 'Fourth downs, two-point tries, kicks, and timeouts. Priced in win probability.' },
  { id: 'offense', label: 'Play calling', what: 'Run or pass in the situations where it matters most. Priced in expected points against the best call.' },
  { id: 'special_teams', label: 'Special teams', what: 'Field goal attempts by distance and weather, kickoff choices. Priced in win probability.' },
];

function Seg<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: [T, string][] }) {
  return <div className="seg">{options.map(([id, label]) => <button key={id} onClick={() => onChange(id)} className={`seg-item ${value === id ? 'seg-item-on' : ''}`}>{label}</button>)}</div>;
}

export function TrendsPage() {
  const [rows, setRows] = useState<LeaderboardRow[] | null>(null);
  const [seasons, setSeasons] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [league, setLeague] = useState<League>('nfl');
  const [phase, setPhase] = useState('head_coach');
  // Undefined means the latest graded season. The resolved one is held separately so
  // writing it back does not refetch and blank the table that just arrived.
  const [season, setSeason] = useState<number | undefined>(undefined);
  const [resolved, setResolved] = useState<number | null>(null);
  const load = () => {
    setError(null); setRows(null);
    return api.leaderboard(season, phase, league)
      .then((r) => { setRows(r.coaches); setSeasons(r.seasons); setResolved(r.season); })
      .catch((e: Error) => setError(e.message));
  };
  useEffect(() => { void load(); }, [league, phase, season]); // eslint-disable-line react-hooks/exhaustive-deps
  const playcalls = phase === 'offense';
  const current = PHASES.find((p) => p.id === phase)!;
  return (
    <>
      <PageBanner>
        <BannerHead
          eyebrow="Coach grades"
          title="Every call, graded."
          sub="Each decision is compared to what usually works in that exact situation, not to how the play turned out. A punt that happened to work is still a bad punt if going for it was the better call."
          aside={
            <div className="grid grid-cols-2 gap-x-5">
              {([['A', 'Optimal', 'the best available call', 'bg-green'], ['B', 'Acceptable', 'close to the best', 'bg-ink'], ['C', 'Questionable', 'clearly worse', 'bg-amber'], ['F', 'Wrong', 'cost real win probability', 'bg-red']] as const).map(([l, n, d, tone]) => (
                <div key={l} className="flex items-start gap-2.5 border-t border-white/25 py-2.5">
                  <span className={`score flex h-8 w-8 shrink-0 items-center justify-center rounded-sm text-[19px] leading-none text-paper ${tone}`}>{l}</span>
                  <div className="min-w-0">
                    <p className="font-cond text-[14px] font-bold uppercase tracking-[0.06em]">{n}</p>
                    <p className="text-[11px] leading-snug text-paper/75">{d}</p>
                  </div>
                </div>
              ))}
            </div>
          }
        />
      </PageBanner>
      <section className="section">
        <div className="container">
          {/* Three separate choices. Run together unlabelled they read as one long
              row of buttons where picking NFL and picking Play calling look like the
              same kind of act, so each one is named and boxed off. */}
          <SectionHead
            title="Who manages a game best."
            eyebrow="Leaderboard"
            action={
              <div className="flex flex-wrap items-center gap-2">
                {seasons.length > 0 && (
                  <Select label="Season" value={season ?? resolved ?? ''}
                          onChange={(v) => setSeason(Number(v))}
                          options={[...seasons].reverse().map((y) => [y, `${y} season`] as const)} />
                )}
                <Seg value={league} onChange={(l) => { setSeason(undefined); setLeague(l); }}
                     options={[['nfl', 'NFL'], ['cfb', 'CFB']]} />
              </div>
            }
          />
          <div className="mb-4">
            <Seg value={phase} onChange={setPhase} options={PHASES.map((p) => [p.id, p.label])} />
          </div>
          {/* max-w was wide enough to leave "per game" alone on a second line */}
          <p className="body-muted mb-5 max-w-none text-[14px]">{current.what} Ranked best to worst by cost per game.</p>
          {error ? <ErrorLine onRetry={load} /> : rows === null ? <LoadingLine active /> : (
            <DataTable rows={rows} rowKey={(r) => r.coach} empty="No graded season yet."
              mobile={(r, i) => (
                <Link to={`/coach/${encodeURIComponent(r.coach)}?league=${league}&season=${season ?? ''}`} className="card-hover flex flex-col gap-3 overflow-hidden">
                  <div className="flex items-center gap-3 px-4 pt-4">
                    <span className={`score flex h-9 w-9 shrink-0 items-center justify-center rounded-sm text-[17px] leading-none ${i < 3 ? 'bg-blue text-paper' : 'bg-wash text-muted'}`}>{i + 1}</span>
                    {r.team_logo && <img src={logo(r.team_logo, 36)} alt="" className="h-9 w-9 shrink-0 object-contain" loading="lazy" />}
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-display text-[17px] font-bold leading-tight">{r.coach}</p>
                      <p className="meta truncate !text-[11px]">{r.team_name ?? r.team}  {r.games} games</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3 border-t border-line px-4 py-3"><Stat label="Optimal" value={`${((r.optimal_rate ?? 0) * 100).toFixed(0)}%`} tone="text-blue" /><Stat label={playcalls ? 'Pts/g' : 'WP/g'} value={playcalls ? fmtSigned(r.epa_per_game, 1, '') : fmtSigned(r.wp_impact_per_game_pct, 1, '%')} tone="text-red" /><Stat label="Wrong" value={r.wrong} tone={r.wrong ? 'text-red' : ''} /></div>
                  <div className="h-1 w-full" style={{ background: teamColor(r.team_color) }} />
                </Link>
              )}
              columns={[
                { key: 'rank', header: '#', render: (r) => <span className="label">{rows.indexOf(r) + 1}</span> },
                { key: 'coach', header: 'Coach', minWidth: 220, render: (r) => (
                  <Link to={`/coach/${encodeURIComponent(r.coach)}?league=${league}&season=${season ?? ''}`} className="flex items-center gap-3 font-display font-bold text-ink hover:text-blue">
                    {r.team_logo && <img src={logo(r.team_logo, 36)} alt="" className="h-8 w-8 object-contain" loading="lazy" />}<span>{r.coach}</span>
                  </Link>) },
                { key: 'team', header: 'Team', hideSm: true, render: (r) => r.team_name ?? r.team },
                { key: 'games', header: 'Games', align: 'right', hideSm: true, render: (r) => r.games },
                { key: 'decisions', header: 'Calls', align: 'right', hideSm: true, render: (r) => r.decisions },
                { key: 'opt', header: 'Optimal', align: 'right', key_stat: true, render: (r) => `${((r.optimal_rate ?? 0) * 100).toFixed(0)}%` },
                { key: 'wp', header: playcalls ? 'Points / game' : 'WP / game', align: 'right', render: (r) => (playcalls ? fmtSigned(r.epa_per_game, 2, '') : fmtSigned(r.wp_impact_per_game_pct, 2, '%')) },
                { key: 'wrong', header: 'Wrong', align: 'right', render: (r) => <span className={r.wrong ? 'text-red' : ''}>{r.wrong}</span> },
              ]} />
          )}
        </div>
      </section>
    </>
  );
}
